import React, { useEffect, useMemo, useState, ReactNode } from 'react'
import './CallBlockingStyle.css'
import useCallBlocking, { type ICallBlockingHooks } from './useCallBlocking'
import SettingsSkeleton from '@pages/Settings/SettingsSkeleton.page'
import useCommunicationsLeaveGuard from '../shared/useCommunicationsLeaveGuard'
import BlockNumberModal from './BlockNumberModal.component'
import { normalizePhoneNumber } from '@utils/numberFormatter.util'
import {
  ActionButton,
  Badge,
  Button,
  DataTable,
  EmptyState,
  Modal,
  SearchWithFilter,
  ToggleSwitch,
  type DataTableColumn,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { Icon as BlockedIcon } from '@enamelai/design-system/icons/Blocked'
import { Icon as Delete2Icon } from '@enamelai/design-system/icons/Delete2'

type BlockedGridRow = ICallBlockingHooks['gridRows'][number]
type BlockedTableRow = { id: string }
type BlockSortColumn = 'number_display' | 'date_display' | 'reason'

const DEFAULT_PAGE_SIZE = 10

const textSortValue = (value?: string | null): string | null => {
  const trimmed = value?.trim()
  return trimmed ? trimmed.toLowerCase() : null
}

const compareSortValues = (
  aValue: string | number | null,
  bValue: string | number | null,
  direction: DataTableSortDirection,
): number => {
  const multiplier = direction === 'asc' ? 1 : -1
  if (aValue === bValue) return 0
  if (aValue === null) return 1
  if (bValue === null) return -1
  return aValue < bValue ? -multiplier : multiplier
}

const sortRows = <T,>(
  rows: T[],
  active: boolean,
  direction: DataTableSortDirection,
  getValue: (row: T) => string | number | null,
): T[] => {
  if (!active) return rows
  return [...rows].sort((left, right) =>
    compareSortValues(getValue(left), getValue(right), direction),
  )
}

const blockedDateValue = (row: BlockedGridRow): number | null => {
  const raw = row.date_time_created_at
  if (!raw) return null
  const time = raw instanceof Date ? raw.getTime() : Date.parse(String(raw))
  return Number.isNaN(time) ? null : time
}

const getBlockSortValue = (
  row: BlockedGridRow,
  column: BlockSortColumn,
): string | number | null => {
  switch (column) {
    case 'number_display':
      return textSortValue(normalizePhoneNumber(row.number_display))
    case 'date_display':
      return blockedDateValue(row)
    case 'reason':
      return textSortValue(row.reason)
  }
}

const useSortedPage = (): {
  currentPage: number
  pageSize: number
  sortColumnId: BlockSortColumn | null
  sortDirection: DataTableSortDirection
  handleSort: (columnId: string) => void
  setCurrentPage: (page: number) => void
  onPageSizeChange: (size: number) => void
} => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortColumnId, setSortColumnId] = useState<BlockSortColumn | null>(null)
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')

  const handleSort = (columnId: string): void => {
    const column = columnId as BlockSortColumn
    if (sortColumnId !== column) {
      setSortColumnId(column)
      setSortDirection('asc')
    } else if (sortDirection === 'asc') {
      setSortDirection('desc')
    } else {
      setSortColumnId(null)
    }
    setCurrentPage(1)
  }

  const onPageSizeChange = (size: number): void => {
    setPageSize(size)
    setCurrentPage(1)
  }

  return {
    currentPage,
    pageSize,
    sortColumnId,
    sortDirection,
    handleSort,
    setCurrentPage,
    onPageSizeChange,
  }
}

const CallBlocking = (): React.ReactElement => {
  const hooks = useCallBlocking()
  const [pendingRemove, setPendingRemove] = useState<{
    id: string
    numberDisplay: string
  } | null>(null)
  const {
    currentPage,
    pageSize,
    sortColumnId,
    sortDirection,
    handleSort,
    setCurrentPage,
    onPageSizeChange,
  } = useSortedPage()

  const {
    setHeaderTitle,
    setHeaderButtons,
    hasChanges,
    handleReset,
    handleSave,
    loading,
  } = hooks
  const leaveGuard = useCommunicationsLeaveGuard(hasChanges && hooks.dataReady)

  const blockedEntries = useMemo(
    () =>
      hooks.gridRows.map((row, index) => ({
        id: row.blocked_user_id || `blocked-${index}`,
        row,
      })),
    [hooks.gridRows],
  )
  const blockedById = new Map(
    blockedEntries.map((entry) => [entry.id, entry.row]),
  )
  const renderBlocked =
    (render: (row: BlockedGridRow) => ReactNode) =>
    (row: BlockedTableRow): ReactNode => {
      const blocked = blockedById.get(row.id)
      return blocked ? render(blocked) : null
    }

  useEffect(() => {
    setCurrentPage(1)
  }, [hooks.mainSearchQuery, hooks.mainFilters, setCurrentPage])

  useEffect(() => {
    setHeaderTitle('Call blocking')
    setHeaderButtons(
      <>
        <Button
          size="sm"
          type="button"
          variant="secondary"
          disabled={!hasChanges || loading || !hooks.dataReady}
          onClick={() => leaveGuard.requestLeave(handleReset)}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          type="button"
          variant="primary"
          disabled={!hasChanges || loading || !hooks.dataReady}
          onClick={() => {
            void handleSave()
          }}
        >
          Save changes
        </Button>
      </>,
    )
    return () => {
      setHeaderTitle(null)
      setHeaderButtons(null)
    }
  }, [
    hasChanges,
    handleReset,
    handleSave,
    hooks.dataReady,
    leaveGuard.requestLeave,
    loading,
    setHeaderButtons,
    setHeaderTitle,
  ])

  const columns: DataTableColumn<BlockedTableRow>[] = [
    {
      id: 'number_display',
      label: 'Phone number',
      width: '30%',
      sortable: true,
      renderCell: renderBlocked((row) => row.number_display),
    },
    {
      id: 'date_display',
      label: 'Blocked date',
      width: '30%',
      sortable: true,
      renderCell: renderBlocked((row) => row.date_display),
    },
    {
      id: 'reason',
      label: 'Reason to block',
      width: '30%',
      sortable: true,
      renderCell: renderBlocked((row) => (
        <Badge mode="tinted" color="red" label={row.reason} />
      )),
    },
  ]

  const sortedBlocked = useMemo(
    () =>
      sortRows(blockedEntries, sortColumnId !== null, sortDirection, (entry) =>
        sortColumnId ? getBlockSortValue(entry.row, sortColumnId) : null,
      ),
    [blockedEntries, sortColumnId, sortDirection],
  )
  const totalPages = Math.max(1, Math.ceil(sortedBlocked.length / pageSize))
  const pageNumber = Math.min(currentPage, totalPages)
  const pageRows = useMemo(
    (): BlockedTableRow[] =>
      sortedBlocked
        .slice((pageNumber - 1) * pageSize, pageNumber * pageSize)
        .map((entry) => ({ id: entry.id })),
    [sortedBlocked, pageNumber, pageSize],
  )

  if (!hooks.dataReady) {
    return (
      <div className="scp-call-blocking-container" id="settings-call-blocking">
        {leaveGuard.modal}
        <SettingsSkeleton variant="table" pageKey="comm-call-block" />
      </div>
    )
  }

  return (
    <div className="scp-call-blocking-container" id="settings-call-blocking">
      {leaveGuard.modal}
      <div className="scp-cbc-filter">
        <span className="scp-cbc-filter-title">Call filtering</span>
        <div className="scp-cbc-toggle-row">
          <div className="scp-cbc-toggle-item">
            <span>Block anonymous / unknown caller IDs</span>
            <ToggleSwitch
              id="unknown-caller-ids"
              checked={hooks.filters.block_unknown_callers}
              aria-label="Block anonymous and unknown caller IDs"
              onChange={(checked) => {
                hooks.handleFilterChange('block_unknown_callers', checked)
              }}
            />
          </div>
          <div className="scp-cbc-toggle-item">
            <span>Block international calls</span>
            <ToggleSwitch
              id="international-calls"
              checked={hooks.filters.block_international_calls}
              aria-label="Block international calls"
              onChange={(checked) => {
                hooks.handleFilterChange('block_international_calls', checked)
              }}
            />
          </div>
        </div>
      </div>

      <div className="scp-table-group">
        {!hooks.loading &&
        hooks.gridRows.length === 0 &&
        hooks.blockedRows.length === 0 ? null : (
          <div className="scp-table-title-container">
            <span className="scp-table-title">Blocked numbers</span>
            <div className="component-buttons">
              <SearchWithFilter
                placeholder="Search"
                value={hooks.mainSearchQuery}
                onChange={hooks.setMainSearchQuery}
                filters={{ reason: hooks.reasonFilterChoices }}
                filterLabels={{ reason: 'Reason' }}
                selectedFilters={hooks.mainFilters}
                onFilterChange={hooks.setMainFilters}
              />
              {hooks.blockedRows.length > 0 ? (
                <Button
                  size="sm"
                  type="button"
                  variant="primary"
                  onClick={() => hooks.setShowAddModal(true)}
                >
                  Add number
                </Button>
              ) : null}
            </div>
          </div>
        )}

        {!hooks.dataReady ? null : hooks.gridRows.length === 0 ? (
          <EmptyState
            className="scp-cbc-empty"
            illustration={<BlockedIcon className="empty-icon" />}
            title="No blocked numbers found"
            description={
              hooks.blockedRows.length === 0
                ? "You don't have any blocked numbers on record yet."
                : 'No blocked numbers match your search.'
            }
            actionLabel={
              hooks.blockedRows.length === 0 ? 'Add number' : undefined
            }
            onAction={
              hooks.blockedRows.length === 0
                ? () => hooks.setShowAddModal(true)
                : undefined
            }
          />
        ) : (
          <DataTable<BlockedTableRow>
            rows={pageRows}
            columns={columns}
            actionsLabel="Action"
            renderActions={renderBlocked((row) => (
              <ActionButton
                icon={
                  <Delete2Icon
                    className="ds-action-button__svg"
                    aria-hidden="true"
                  />
                }
                label="Remove"
                tone="danger"
                variant="outline"
                disabled={!row.blocked_user_id}
                onClick={() => {
                  if (!row.blocked_user_id) return
                  setPendingRemove({
                    id: row.blocked_user_id,
                    numberDisplay: row.number_display,
                  })
                }}
              />
            ))}
            sortColumnId={sortColumnId ?? undefined}
            sortDirection={sortColumnId ? sortDirection : undefined}
            onSort={handleSort}
            pagination={{
              currentPage: pageNumber,
              totalPages,
              pageSize,
              onPageChange: setCurrentPage,
              onPageSizeChange,
            }}
          />
        )}
      </div>

      <BlockNumberModal
        show={hooks.showAddModal}
        onHide={() => hooks.setShowAddModal(false)}
        onSuccess={() => hooks.refreshBlockedList()}
        existingBlockedUsers={hooks.blockedRows}
      />

      <Modal
        open={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        size="sm"
        variant="confirmation"
        className="unsaved-changes-modal"
        title="Remove blocked number?"
        icon={<Delete2Icon width={16} height={16} aria-hidden="true" />}
        iconTone="danger"
        description={
          pendingRemove
            ? `${pendingRemove.numberDisplay} will be removed from the blocked list. Are you sure you want to continue?`
            : ''
        }
        primaryLabel="Remove"
        secondaryLabel="Back"
        primaryDestructive
        primaryDisabled={hooks.loading}
        onPrimaryClick={() => {
          if (!pendingRemove) return
          const { id } = pendingRemove
          setPendingRemove(null)
          void hooks.removeBlocked(id)
        }}
        onSecondaryClick={() => setPendingRemove(null)}
      />
    </div>
  )
}

export default CallBlocking
